import orderBy from 'lodash/orderBy'
import { type CSSProperties, useMemo } from 'react'
import { Check, Globe } from 'lucide-react'
import {
  NativeSelect,
  NativeSelectOption,
} from 'tailwind-v4/components/native-select'
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from 'tailwind-v4/components/combobox'
import useLocations from '@/state/entities/locations/hooks/useLocation'
import { type OrganisationLocationType } from '@/connect-types/organisations/organisations.type'

interface PassedProps {
  value: string | string[]
  onChange: (value: string | string[]) => void
  mode?: 'multiple' | 'tags'
  style?: CSSProperties
  reset?: boolean
  resetText?: string
  size?: 'sm' | 'md' | 'lg'
  altLocations?: OrganisationLocationType[]
  disabledLocations?: string[]
  inner?: boolean
  /**
   * Label shown as a pill when nothing is selected — for cases where an empty
   * selection is a meaningful state (e.g. "All venues" = applies everywhere).
   * Multiple-mode only.
   */
  emptyStateLabel?: string
}

interface VenueOption {
  value: string
  label: string
}

function LocationSelect({
  value,
  onChange,
  mode,
  reset,
  resetText,
  altLocations,
  disabledLocations,
  emptyStateLabel,
}: PassedProps) {
  const locations = useLocations()
  const anchorRef = useComboboxAnchor()

  const options = useMemo<OrganisationLocationType[]>(
    () =>
      orderBy(
        altLocations ?? locations,
        ['alias'],
        ['asc']
      ) as OrganisationLocationType[],
    [locations, altLocations]
  )

  // `{ value, label }`-shaped items let the combobox derive its label/value
  // automatically. Kept referentially stable so the controlled `value`
  // (filtered from the same array below) matches by identity.
  const items = useMemo<VenueOption[]>(
    () =>
      options.map(({ serial, alias }) => ({
        value: serial,
        label: alias || serial,
      })),
    [options]
  )

  const selectedItems = useMemo(() => {
    const selected = new Set(Array.isArray(value) ? value : [])
    return items.filter((item) => selected.has(item.value))
  }, [items, value])

  if (mode === 'tags') {
    return (
      <NativeSelect
        className="w-full"
        value={typeof value === 'string' ? value : ''}
        onChange={({ target }) => {
          if (!target.value) {
            onChange(null as unknown as string)
            return
          }
          onChange(target.value)
        }}
      >
        {reset ? (
          <NativeSelectOption value="">
            {resetText ?? 'No venue selected'}
          </NativeSelectOption>
        ) : null}
        {options.map(({ alias, serial }) => (
          <NativeSelectOption
            key={serial}
            value={serial}
            disabled={disabledLocations?.includes(serial)}
          >
            {alias || serial}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    )
  }

  return (
    <Combobox<VenueOption, true>
      items={items}
      multiple
      value={selectedItems}
      onValueChange={(next) => {
        onChange(next.map((item) => item.value))
      }}
    >
      <ComboboxChips ref={anchorRef} className="min-h-10">
        {selectedItems.length === 0 && emptyStateLabel ? (
          <span className="inline-flex items-center rounded-sm bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground">
            {emptyStateLabel}
          </span>
        ) : null}
        <ComboboxValue>
          {(selected: VenueOption[]) =>
            selected.map((item) => (
              // No inline remove-X — deselect by unticking in the list. This
              // stops a click meant to dismiss the popover from accidentally
              // dropping a selection.
              <ComboboxChip
                key={item.value}
                aria-label={item.label}
                showRemove={false}
              >
                {item.label}
              </ComboboxChip>
            ))
          }
        </ComboboxValue>
        <ComboboxChipsInput placeholder="Select venues…" />
      </ComboboxChips>
      <ComboboxContent anchor={anchorRef}>
        {/* Pinned "all venues" reset — clears the selection (empty array = all
            venues to the backend). Always available, above the search results. */}
        {emptyStateLabel ? (
          <div className="border-b border-border p-1">
            <button
              type="button"
              onClick={() => {
                onChange([])
              }}
              className="flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-sm text-foreground transition-colors hover:bg-accent"
            >
              <Globe className="size-4 shrink-0 text-muted-foreground" />
              <span className="flex-1 text-left">{emptyStateLabel}</span>
              {selectedItems.length === 0 ? (
                <Check className="size-4 shrink-0" />
              ) : null}
            </button>
          </div>
        ) : null}
        <ComboboxEmpty>No venues found</ComboboxEmpty>
        <ComboboxList>
          {(item: VenueOption) => (
            <ComboboxItem key={item.value} value={item}>
              {item.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

export default LocationSelect
